import type { GetCountryOriginResult } from '@peekapak/types/DataProtocolTypes';
import type { DataResidency, HomeCountry } from '@peekapak/common/lib/regionAndResidency';
import {
  resolveHomeCountry,
  type HomeCountryResolution,
  type HomeCountrySignals,
  type HomeCountrySource,
} from '@peekapak/common/lib/homeCountryResolution';
import { alpha2ToAlpha3, countryNameForAlpha3 } from './countryCode';

/**
 * "Where is this visitor from?" is ONE operation shared by marketing, sign-up,
 * and log-in (design D12). This module is the single app-side country-of-origin
 * resolver: it gathers the pre-login signals (explicit prior choice, browser
 * geolocation, IP guess), delegates precedence + residency derivation to the
 * pure `resolveHomeCountry` in `@peekapak/common`, and returns the canonical
 * ISO 3166-1 alpha-3 country plus its display name, data residency, and source.
 *
 * Canonical country codes are alpha-3 (D13). The IP signal's alpha-2 is
 * converted to alpha-3 here via `i18n-iso-countries` (D15) - geo-places is used
 * only for the browser-geolocation coordinates path. Geolocation prompting is
 * context-aware (D14): callers pass `mayPromptForGeolocation` so auth pages may
 * prompt while cold marketing pages never do.
 */

// The get-country-origin endpoint URL. There is deliberately no default: every
// app's backend lives at an absolute, residency-aware URL, so each app must
// call `configureCountryOriginEndpoint` before resolving (both apps do so in
// their BackendInterface). An unconfigured resolver throws - the per-signal
// error handling degrades the result to UNK, but the console says exactly what
// is wrong instead of a misleading JSON parse error from an HTML fallback page.
// A function is supported so an app whose URL changes at runtime (e.g. on a
// residency switch) is always resolved lazily.
let resolveEndpoint: () => string = () => {
  throw new Error(
    'country-origin endpoint not configured: call configureCountryOriginEndpoint() before resolving (see BackendInterface)',
  );
};

/**
 * Point the resolver at a different get-country-origin endpoint. Pass a string
 * for a fixed URL, or a function when the URL is computed at call time.
 */
export function configureCountryOriginEndpoint(endpoint: string | (() => string)): void {
  resolveEndpoint = typeof endpoint === 'function' ? endpoint : () => endpoint;
}

// localStorage key for the user's explicit home-country choice. It is the
// highest-precedence signal and wins on subsequent visits.
export const HOME_COUNTRY_CHOICE_KEY = 'peekapak.homeCountryChoice';

// getCurrentPosition can hang indefinitely; bound it so resolution never stalls.
const GEOLOCATION_TIMEOUT_MS = 10_000;
const GEOLOCATION_MAX_AGE_MS = 5 * 60 * 1000;

export interface CountryOfOrigin {
  /** Canonical ISO 3166-1 alpha-3 country (`UNK` when no signal resolved). */
  countryOfOrigin: HomeCountry;
  /** English display name for `countryOfOrigin`; empty when unknown. */
  countryName: string;
  /** Data residency derived from `countryOfOrigin`. */
  dataResidency: DataResidency;
  /** Which signal produced the result (chosen vs detected vs default). */
  source: HomeCountrySource;
}

export interface ResolveCountryOfOriginOptions {
  /**
   * Whether the browser geolocation permission prompt may be shown (D14).
   * `true` on sign-up/log-in; `false` (default) on marketing pages, where
   * geolocation is used only if permission was already granted.
   */
  mayPromptForGeolocation?: boolean;
}

const normalizeAlpha3 = (value: string | null | undefined): HomeCountry | undefined => {
  const trimmed = value?.trim().toUpperCase();
  return trimmed ? (trimmed as HomeCountry) : undefined;
};

/** Read the persisted explicit home-country choice (top-precedence signal). */
export function readHomeCountryChoice(): HomeCountry | undefined {
  try {
    return normalizeAlpha3(localStorage.getItem(HOME_COUNTRY_CHOICE_KEY));
  } catch (error) {
    console.warn('[countryOfOrigin] failed to read stored choice:', error);
    return undefined;
  }
}

/** Persist an explicit home-country choice so it wins on subsequent visits. */
export function persistHomeCountryChoice(alpha3: HomeCountry): void {
  try {
    localStorage.setItem(HOME_COUNTRY_CHOICE_KEY, alpha3.trim().toUpperCase());
  } catch (error) {
    console.warn('[countryOfOrigin] failed to persist choice:', error);
  }
}

/** Clear the explicit choice, reverting to inferred (detected) resolution. */
export function clearHomeCountryChoice(): void {
  try {
    localStorage.removeItem(HOME_COUNTRY_CHOICE_KEY);
  } catch (error) {
    console.warn('[countryOfOrigin] failed to clear choice:', error);
  }
}

/**
 * Resolve the visitor's country of origin from the available pre-login signals.
 * An explicit prior choice wins outright, so the lower-precedence geolocation
 * and IP lookups are skipped entirely when one is present.
 */
export async function resolveCountryOfOrigin(options: ResolveCountryOfOriginOptions = {}): Promise<CountryOfOrigin> {
  const { mayPromptForGeolocation = false } = options;

  const explicitChoice = readHomeCountryChoice();
  const signals: HomeCountrySignals = { explicitChoice };

  if (!explicitChoice) {
    // Geolocation and IP are independent lookups; gather them concurrently and
    // let resolveHomeCountry apply precedence (geolocation > IP).
    const [geolocationCountry, ipCountry] = await Promise.all([
      resolveGeolocationCountry(mayPromptForGeolocation),
      resolveIpHomeCountry(),
    ]);
    signals.geolocationCountry = geolocationCountry;
    signals.ipCountry = ipCountry;
  }

  return toCountryOfOrigin(resolveHomeCountry(signals));
}

function toCountryOfOrigin(resolution: HomeCountryResolution): CountryOfOrigin {
  return {
    countryOfOrigin: resolution.homeCountry,
    countryName: countryNameForAlpha3(resolution.homeCountry),
    dataResidency: resolution.dataResidency,
    source: resolution.source,
  };
}

/**
 * IP signal: the backend IP guess (alpha-2 `country_code`) converted to the
 * canonical alpha-3, or undefined on failure. This is the single IP-signal
 * source - the app-init residency detection (`DataResidencyManager`) reuses it
 * rather than re-fetching the endpoint with its own mapping. It never touches
 * geolocation/geo-places, so it is safe to call on cold marketing pages (D14).
 */
export async function resolveIpHomeCountry(): Promise<HomeCountry | undefined> {
  try {
    const data = await fetchCountryOrigin(resolveEndpoint());
    return alpha2ToAlpha3(data.country_code);
  } catch (error) {
    console.warn('[countryOfOrigin] IP country lookup failed:', error);
    return undefined;
  }
}

/**
 * Geolocation signal: browser coordinates reverse-geocoded by the backend to a
 * canonical alpha-3 (`homeCountry`) via geo-places. Returns undefined when
 * geolocation is unavailable, blocked, or not permitted by the prompt policy.
 */
async function resolveGeolocationCountry(mayPrompt: boolean): Promise<HomeCountry | undefined> {
  try {
    const coordinates = await getBrowserCoordinates(mayPrompt);
    if (!coordinates) {
      return undefined;
    }
    const url = `${resolveEndpoint()}?latitude=${coordinates.latitude}&longitude=${coordinates.longitude}`;
    const data = await fetchCountryOrigin(url);
    return normalizeAlpha3(data.homeCountry);
  } catch (error) {
    console.warn('[countryOfOrigin] geolocation country lookup failed:', error);
    return undefined;
  }
}

async function fetchCountryOrigin(url: string): Promise<GetCountryOriginResult> {
  const response = await window.fetch(url);
  if (!response.ok) {
    throw new Error(`get-country-origin request failed: HTTP ${response.status} ${response.statusText}`);
  }
  return (await response.json()) as GetCountryOriginResult;
}

interface Coordinates {
  latitude: number;
  longitude: number;
}

/**
 * Obtain browser coordinates subject to the prompt policy (D14). Already-granted
 * permission resolves silently; when not yet granted, the browser prompt is only
 * triggered where the policy allows it (auth pages). A `denied` permission never
 * prompts and yields undefined so the caller falls back to the IP signal.
 */
async function getBrowserCoordinates(mayPrompt: boolean): Promise<Coordinates | undefined> {
  if (typeof window === 'undefined' || !window.navigator?.geolocation) {
    return undefined;
  }

  const permission = await getGeolocationPermissionState();
  if (permission === 'denied') {
    return undefined;
  }
  // Calling getCurrentPosition when permission is not already 'granted' shows the
  // browser prompt. Only do so where the policy permits it; marketing pages
  // (mayPrompt=false) never prompt, and an unknown permission state is treated
  // as "may prompt" so it is skipped on marketing too.
  if (permission !== 'granted' && !mayPrompt) {
    return undefined;
  }

  return getCurrentPosition();
}

async function getGeolocationPermissionState(): Promise<PermissionState | undefined> {
  try {
    if (typeof window === 'undefined' || !window.navigator?.permissions?.query) {
      return undefined;
    }
    const status = await window.navigator.permissions.query({
      name: 'geolocation' as PermissionName,
    });
    return status.state;
  } catch {
    // Some browsers reject/omit the geolocation permission descriptor.
    return undefined;
  }
}

function getCurrentPosition(): Promise<Coordinates | undefined> {
  return new Promise((resolve) => {
    window.navigator.geolocation.getCurrentPosition(
      (position) =>
        resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        }),
      (error) => {
        console.warn('[countryOfOrigin] geolocation unavailable:', error.message);
        resolve(undefined);
      },
      { timeout: GEOLOCATION_TIMEOUT_MS, maximumAge: GEOLOCATION_MAX_AGE_MS },
    );
  });
}
